import { EntityTypeCode, useRequestUrlBuilder, useViewerEntity } from '@rahtab/auth/lib/token-based-auth';
import { Screen, StackScreenProps } from '@rahtab/stack-navigator';
import {
  Button,
  ButtonBase,
  Card,
  CardCaption,
  CardContainer,
  getSpacing,
  ImageButton,
  nationalIdValidation,
  Text,
  TextInput,
  ToggleButton,
} from '@rahtab/ui';
import AccountAvatarIcon from '@rahtab/ui/dist/icons/AccountAvatarIcon';
import BillingIcon from '@rahtab/ui/dist/icons/BillingIcon';
import CardScanIcon from '@rahtab/ui/dist/icons/CardScanIcon';
import CustomerAffairsIcon from '@rahtab/ui/dist/icons/CustomerAffairsIcon';
import InventoryIcon from '@rahtab/ui/dist/icons/InventoryIcon';
import OrderingIcon from '@rahtab/ui/dist/icons/OrderingIcon';
import SaleHistoryIcon from '@rahtab/ui/dist/icons/SaleHistoryIcon';
import SupportIcon from '@rahtab/ui/dist/icons/SupportIcon';
import RahtabEnglishLogotype from '@rahtab/ui/dist/logos/RahtabEnglishLogotype';
import { RouteProp } from '@react-navigation/native';
import useLinkBuilder from 'hooks/useLinkBuilder';
import React, { ReactElement, useEffect, useMemo, useState, VFC } from 'react';
import { FlatList, Linking, StyleSheet, View } from 'react-native';
import { RootStackType } from '../../Router';
import ProviderHeaderCard from './ProviderHeaderCard';
import QRScannerBottomSheet from './QRScannerBottomSheet';
import { UserType } from './useCheckUser';
import { useHome } from './useHome';

const iranianCustomer = 'شهروند ایرانی';
const foreignCustomer = 'اتباع خارجه';

export const homeScreenButtons: HomeScreenButton[] = [
  {
    enTitle: 'saleHistory',
    faTitle: 'سوابق فروش',
    svg: <SaleHistoryIcon />,
    link: 'SaleHistory',
  },
  {
    enTitle: 'registerNewCustomer',
    faTitle: 'امور خریداران',
    svg: <CustomerAffairsIcon />,
    link: 'RegisterCustomers',
  },
  {
    enTitle: 'billing',
    faTitle: 'صورت حساب',
    svg: <BillingIcon />,
    link: 'Billing',
    middlemanHasAccess: true,
  },
  {
    enTitle: 'availableItems',
    faTitle: 'موجودی',
    svg: <InventoryIcon />,
    link: 'StoreInventory',
  },
  {
    enTitle: 'submitOrder',
    faTitle: 'سفارش‌گذاری',
    svg: <OrderingIcon />,
    link: 'OrderCategories',
    middlemanHasAccess: true,
  },
  {
    faTitle: 'پشتیبانی',
    svg: <SupportIcon />,
    link: 'Support',
    enTitle: 'support',
    middlemanHasAccess: true,
  },
];

type Customer = typeof iranianCustomer | typeof foreignCustomer;
const Home: VFC<HomeProps> = ({ route }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selected, setSelected] = useState('');
  const [rahtabCodeLock, setRahtabCodeLock] = useState(false);
  const [cardBottomSheet, setCardBottomSheet] = useState(false);
  const { viewerSelectedEntity } = useViewerEntity();

  useEffect(() => {
    if (!username) {
      setRahtabCodeLock(false);
    }
  }, [username]);

  useEffect(() => {
    if (route.params && route.params.qrData) {
      setUsername(route.params.qrData);
      setRahtabCodeLock(true);
    }
  }, [route.params]);
  const handleDisableButton = () => {
    if (!/^\d{4}$/.test(password)) return true;
    if (rahtabCodeLock) return false;
    if (customerType === iranianCustomer && !nationalIdValidation.required().isValidSync(username)) return true;
    if (customerType === foreignCustomer && !/^\d{7,}$/.test(username)) return true;
    return false;
  };
  const { screenProps, userVerified, checkUser } = useHome();
  useEffect(() => {
    setUsername('');
    setPassword('');
  }, [userVerified]);
  const [customerType, setCustomerType] = useState<Customer>(iranianCustomer);
  const linkBuilder = useLinkBuilder<RootStackType>();
  const handleCheckUser = () => {
    let userType: typeof UserType[keyof typeof UserType];
    if (rahtabCodeLock) {
      userType = UserType.CARD;
    } else if (customerType === iranianCustomer) {
      userType = UserType.CITIZEN;
    } else {
      userType = UserType.IMMIGRANT;
    }
    checkUser({ userId: username, userPass: password, userType });
  };
  const navigationButtons = useMemo(() => {
    // if (!screenProps.data?.orderPermission)
    //   return homeScreenButtons.filter((button) => button.enTitle !== 'submitOrder');
    return homeScreenButtons
      .filter((item) => (viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? item.middlemanHasAccess : true))
      .filter((item) => (item.enTitle === 'submitOrder' ? screenProps.data?.orderPermission : true));
  }, [screenProps.data?.orderPermission, viewerSelectedEntity?.typeCode]);

  const submit = (code: string) => {
    setUsername(code);
    setRahtabCodeLock(true);
  };

  const bottomSheetCloseReq = () => {
    setCardBottomSheet(false);
  };

  const requestUrlBuilder = useRequestUrlBuilder();
  const openBillingWebPage = async () => {
    const requestpaymentUrl = await requestUrlBuilder('RequestPayment');
    Linking.openURL(requestpaymentUrl);
  };

  return (
    <Screen
      {...screenProps}
      renderHeader={() => {
        return (
          <View style={styles.header}>
            {!screenProps.loading && (
              <ButtonBase testID="profileButton" screenName="Profile">
                <AccountAvatarIcon />
              </ButtonBase>
            )}
            <View style={styles.rahtabLogoStyle}>
              <RahtabEnglishLogotype height={25} />
            </View>
          </View>
        );
      }}
      renderControls={(providerData) => {
        return (
          <ProviderHeaderCard
            id={providerData.id}
            name={providerData.name}
            image={providerData.image}
            premiumProvider
            rate={providerData.rate}
            rateCount={providerData.numberOfRates}
            ranking={providerData.ranking}
          />
        );
      }}
      renderMain={({ hasNTSWCode, isNTSWMember, ranking }) => {
        return (
          <>
            <QRScannerBottomSheet visible={cardBottomSheet} onSubmit={submit} onRequestClose={bottomSheetCloseReq} />
            <FlatList
              keyboardShouldPersistTaps="handled"
              ListHeaderComponentStyle={{ paddingHorizontal: getSpacing(0.5), paddingTop: getSpacing() }}
              ListHeaderComponent={
                <>
                  {ranking === 0 && (
                    <Card style={{ marginBottom: getSpacing() }}>
                      <CardCaption color="primary">
                        <Text color="white" margin="none">
                          توجه
                        </Text>
                      </CardCaption>
                      <CardContainer>
                        <Text style={{ textAlign: 'justify' }} margin="none">
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کننده' : 'پذیرنده'}{' '}
                          گرامی پیرو نرخ وفاداری و جذب سهمیه شما در طول سال ۱۴۰۱، سطح{' '}
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کنندگی' : 'پذیرندگی'}{' '}
                          شما در سطح «<Text color="secondary">پایه</Text>» باقی مانده است و سایر{' '}
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کنندگان' : 'پذیرندگان'}{' '}
                          با عملکرد بهتر به سطح ممتاز ارتقا یافته‌اند. مزایای سطح ممتاز به شرح ذیل است:
                        </Text>
                        <Text margin="none">۱- افزایش سهمیه خرید در پایانه‌هایی که عملکرد مناسبی داشته‌اند.</Text>
                        <Text margin="none">
                          ۲- امکان سفارش‌گذاری ۱۵ دقیقه زودتر از{' '}
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کنندگان' : 'پذیرندگان'}{' '}
                          سطح عادی
                        </Text>
                      </CardContainer>
                    </Card>
                  )}
                  {ranking === 1 && (
                    <Card style={{ marginBottom: getSpacing() }}>
                      <CardCaption color="primary">
                        <Text color="white" margin="none">
                          توجه
                        </Text>
                      </CardCaption>
                      <CardContainer>
                        <Text style={{ textAlign: 'justify' }} margin="none">
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کننده' : 'پذیرنده'}{' '}
                          گرامی پیرو نرخ وفاداری و جذب سهمیه شما در طول سال ۱۴۰۱، سطح{' '}
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کنندگی' : 'پذیرندگی'}{' '}
                          شما به سطح «<Text color="success">ممتاز</Text>» ارتقا پیدا کرده است. مزایای سطح ممتاز به شرح
                          ذیل است:
                        </Text>
                        <Text margin="none">۱- افزایش سهمیه خرید در پایانه‌هایی که عملکرد مناسبی داشته‌اید.</Text>
                        <Text margin="none">
                          ۲- امکان سفارش‌گذاری ۱۵ دقیقه زودتر از{' '}
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کنندگان' : 'پذیرندگان'}{' '}
                          سطح عادی
                        </Text>
                      </CardContainer>
                    </Card>
                  )}
                  {hasNTSWCode && (
                    <Card style={{ marginBottom: getSpacing() }}>
                      <CardCaption color="secondary">
                        <Text color="white" margin="none">
                          توجه
                        </Text>
                      </CardCaption>
                      <CardContainer>
                        <Text style={{ textAlign: 'justify' }}>
                          {viewerSelectedEntity?.typeCode === EntityTypeCode.Middleman ? 'توزیع‌کننده' : 'پذیرنده'}{' '}
                          گرامی، وضعیت ثبت اطلاعات بارهای ورودی واحدصنفی شما در سامانه جامع تجارت{' '}
                          <Text color="secondary">غیرفعال</Text> می‌باشد. به منظور ایجاد امکان ثبت خودکار اطلاعات بارهای
                          دریافتی در سامانه جامع تجارت، ضروری است که با مراجعه به این قسمت نسبت به درخواست دریافت کد
                          اعتبارسنجی و ثبت آن اقدام نمایید. عدم ثبت کد اعتبارسنجی سامانه جامع تجارت در سامانه رهتاب منجر
                          به محرومیت از امکان سفارش‌گذاری خواهد شد.
                        </Text>
                        <Button
                          variant="outlined"
                          color="secondary"
                          text="فعال‌سازی سامانه جامع تجارت"
                          screenName="NTSW"
                        />
                      </CardContainer>
                    </Card>
                  )}
                  {viewerSelectedEntity?.typeCode === EntityTypeCode.Provider ? (
                    <Card style={{ marginBottom: getSpacing(0.5) }}>
                      <CardCaption>
                        <Text margin="none" style={styles.saleTitle} color="primary">
                          ثبت تراکنش فروش
                        </Text>
                      </CardCaption>
                      <CardContainer>
                        {!rahtabCodeLock && (
                          <ToggleButton
                            color="secondary"
                            selected={customerType}
                            onPress={(type) => {
                              setCustomerType(type as Customer);
                            }}>
                            <ToggleButton.Item label={iranianCustomer} />
                            <ToggleButton.Item label={foreignCustomer} />
                          </ToggleButton>
                        )}
                        <View style={styles.rowView}>
                          <TextInput
                            testID="usernameInput"
                            placeholder={
                              customerType === iranianCustomer
                                ? 'کد ملی خریدار را وارد نمایید.'
                                : 'کد اختصاصی (فیدا) خریدار را وارد نمایید.'
                            }
                            label={
                              rahtabCodeLock ? 'شناسه' : customerType === iranianCustomer ? 'کد ملی' : 'کد اختصاصی'
                            }
                            editable={!rahtabCodeLock}
                            showClearIconOnBlur={rahtabCodeLock}
                            returnKeyLabel="next"
                            onChangeText={setUsername}
                            // eslint-disable-next-line react-native/no-inline-styles
                            style={{ flex: 1 }}
                            onFocus={() => {
                              setSelected('username');
                            }}
                            value={username}
                            keyboardType="number-pad"
                            onSubmitEditing={() => {
                              setSelected('password');
                            }}
                          />
                          <ButtonBase
                            accessibilityLabel="rahtabCard"
                            onPress={() => setCardBottomSheet(true)}
                            children={<CardScanIcon style={styles.cardIcon} />}
                          />
                        </View>
                        <TextInput
                          testID="passwordInput"
                          placeholder="رمز خرید را وارد نمایید."
                          returnKeyLabel="done"
                          secureTextEntry
                          value={password}
                          selected={selected === 'password'}
                          onChangeText={setPassword}
                          keyboardType="number-pad"
                          label="رمز خرید"
                          onSubmitEditing={() => {
                            !handleDisableButton() && handleCheckUser();
                          }}
                        />
                        <Button
                          testID="checkUserButton"
                          disabled={handleDisableButton()}
                          text="شروع عملیات فروش"
                          color="primary"
                          onPress={handleCheckUser}
                        />
                      </CardContainer>
                    </Card>
                  ) : null}
                </>
              }
              keyExtractor={(item) => `homeScreenButton-${item.enTitle}`}
              data={navigationButtons}
              contentContainerStyle={styles.contentContainerStyle}
              numColumns={3}
              renderItem={({ item }) => {
                if (item.enTitle === 'billing') {
                  return <ImageButton svg={item.svg} onPress={openBillingWebPage} name={item.faTitle} />;
                }
                return <ImageButton svg={item.svg} linkUrl={item.link && linkBuilder(item.link)} name={item.faTitle} />;
              }}
            />
          </>
        );
      }}
    />
  );
};

export default Home;

const styles = StyleSheet.create({
  contentContainerStyle: {
    padding: getSpacing(0.5),
    paddingTop: 0,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    height: 30,
  },
  rowView: {
    flexDirection: 'row',
  },
  saleTitle: {
    alignSelf: 'center',
  },
  cardIcon: {
    marginLeft: getSpacing(0.6),
  },
  rahtabLogoStyle: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    height: 32,
  },
});

interface HomeProps extends StackScreenProps<RootStackType> {
  route: RouteProp<RootStackType, 'Home'>;
}
export type HomeScreenTitleType =
  | 'saleHistory'
  | 'registerNewCustomer'
  | 'support'
  | 'info'
  | 'availableItems'
  | 'submitOrder'
  | 'billing'
  | 'logout';
interface HomeScreenButton {
  enTitle: HomeScreenTitleType;
  faTitle: string;
  svg: ReactElement;
  link?: keyof RootStackType;
  middlemanHasAccess?: boolean;
}
